import { useEffect, useState } from "react";
import { icons as lucideIcons, FileText, type LucideIcon } from "lucide-react";
import { PSI_PLATFORM_BE_URL } from "@/hooks/api/config";
import { getValidToken } from "@/hooks/api/auth/tokenManager";

/**
 * Árbol de contenido de la home ("Explorar por categoría") — antes vivía en un
 * archivo TS estático (`src/data/knowledgeBase.ts`), ahora en `kb_category` /
 * `kb_subcategory` / `kb_article` (Supabase, vía psiplatform-be). Ese archivo
 * estático sigue existiendo solo como fuente del script de seed
 * (`psiplatform-be/scripts/seed-knowledge-base.ts`) — nada en la app lo importa.
 *
 * Los tipos acá replican el shape que tenían `Category`/`Subcategory`/`Article`
 * en ese archivo, para que los componentes que ya los consumían (grid, vista
 * de categoría, vista de artículo) no necesiten cambios más allá del import.
 */

export interface ExternalLink {
  label: string;
  url: string;
  icon?: string;
}

/** Un paso del carrusel de una sección `slider` (captura + explicación). */
export interface Slide {
  title: string;
  text: string;
  image: string;
}

export interface ContentSection {
  title: string;
  type:
    | "text"
    | "list"
    | "steps"
    | "warning"
    | "tip"
    | "contact"
    | "columns"
    | "slider";
  /** Opcional: `columns` lleva el cuerpo en `columns` y `slider` en `slides`. */
  content?: string | string[];
  highlight?: boolean;
  subtitle?: string;
  columns?: ContentSection[][];
  slides?: Slide[];
}

export interface ArticleContent {
  intro?: string;
  sections: ContentSection[];
  importantNotes?: string[];
  externalLinks?: ExternalLink[];
}

export interface Article {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  keywords: string[];
  content: ArticleContent;
}

export interface Subcategory {
  id: string;
  title: string;
  icon: LucideIcon;
  description: string;
  articles: Article[];
}

export interface Category {
  id: string;
  title: string;
  icon: LucideIcon;
  description: string;
  articles: Article[];
  subcategories?: Subcategory[];
}

// Resuelve contra TODO el set de lucide-react (no una lista fija) — así
// cualquier ícono elegido desde el backoffice admin funciona sin tener que
// tocar este archivo cada vez que se usa uno nuevo.
function resolveIcon(name: string): LucideIcon {
  return (lucideIcons as Record<string, LucideIcon>)[name] ?? FileText;
}

interface RawArticle {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon: string;
  keywords: string[];
  content: ArticleContent;
}

interface RawSubcategory {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon: string;
  articles: RawArticle[];
}

interface RawCategory {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon: string;
  articles: RawArticle[];
  subcategories: RawSubcategory[];
}

function mapArticle(raw: RawArticle): Article {
  return {
    id: raw.slug,
    title: raw.title,
    description: raw.description,
    icon: resolveIcon(raw.icon),
    keywords: raw.keywords,
    content: raw.content,
  };
}

function mapCategories(raw: RawCategory[]): Category[] {
  return raw.map((category) => ({
    id: category.slug,
    title: category.title,
    description: category.description,
    icon: resolveIcon(category.icon),
    articles: category.articles.map(mapArticle),
    subcategories: category.subcategories.length
      ? category.subcategories.map((sub) => ({
          id: sub.slug,
          title: sub.title,
          description: sub.description,
          icon: resolveIcon(sub.icon),
          articles: sub.articles.map(mapArticle),
        }))
      : undefined,
  }));
}

// Sin cache: se pide a /api/knowledge-base en cada montaje de Home. El
// contenido lo edita un dev vía Supabase y el volumen de tráfico de este
// endpoint es bajo, así que se prioriza ver un cambio al instante (sin TTL
// ni restart) por sobre ahorrarse un fetch por navegación.
let inFlight: Promise<Category[]> | null = null;

async function fetchKnowledgeBase(): Promise<Category[]> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const token = getValidToken();
    const resp = await fetch(`${PSI_PLATFORM_BE_URL}/api/knowledge-base`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) throw new Error("No se pudo cargar la base de conocimiento");
    const data = await resp.json();
    return mapCategories(data.categories as RawCategory[]);
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

export function useKnowledgeBase() {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchKnowledgeBase()
      .then((result) => { if (!cancelled) setCategories(result); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : "Error desconocido"); });
    return () => { cancelled = true; };
  }, []);

  return { categories: categories ?? [], isLoading: categories === null && !error, error };
}

/**
 * Variante SIN auth, contra `/api/knowledge-base/public` — usada por la ruta
 * de preview (`/kb-preview`), que vive fuera del login de psicóloga (la usa
 * el backoffice para previsualizar contenido antes/después de editarlo).
 */
export function useKnowledgeBasePublic() {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${PSI_PLATFORM_BE_URL}/api/knowledge-base/public`)
      .then((resp) => {
        if (!resp.ok) throw new Error("No se pudo cargar la base de conocimiento");
        return resp.json();
      })
      .then((data) => { if (!cancelled) setCategories(mapCategories(data.categories as RawCategory[])); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : "Error desconocido"); });
    return () => { cancelled = true; };
  }, []);

  return { categories: categories ?? [], isLoading: categories === null && !error, error };
}

export function getArticleById(categories: Category[], articleId: string): Article | undefined {
  for (const category of categories) {
    const article = category.articles.find((a) => a.id === articleId);
    if (article) return article;
    if (category.subcategories) {
      for (const sub of category.subcategories) {
        const subArticle = sub.articles.find((a) => a.id === articleId);
        if (subArticle) return subArticle;
      }
    }
  }
  return undefined;
}

export function searchKnowledgeBase(categories: Category[], query: string): Article[] {
  const searchTerm = query.toLowerCase().trim();
  if (!searchTerm) return [];

  const results: Article[] = [];

  const searchArticle = (article: Article) => {
    const matchesTitle = article.title.toLowerCase().includes(searchTerm);
    const matchesDescription = article.description.toLowerCase().includes(searchTerm);
    const matchesKeywords = article.keywords.some((kw) => kw.toLowerCase().includes(searchTerm));
    const matchesContent = article.content.sections.some((section) => {
      // `content` es opcional: las secciones `columns` y `slider` llevan el cuerpo en
      // `columns`/`slides`. Los slides se incluyen acá para que su texto siga siendo
      // buscable; si no, el contenido de un carrusel quedaría invisible al buscador.
      if (typeof section.content === "string") {
        if (section.content.toLowerCase().includes(searchTerm)) return true;
      } else if (Array.isArray(section.content)) {
        if (section.content.some((item) => item.toLowerCase().includes(searchTerm))) return true;
      }
      return (section.slides ?? []).some(
        (slide) =>
          slide.title.toLowerCase().includes(searchTerm) ||
          slide.text.toLowerCase().includes(searchTerm),
      );
    });

    if (matchesTitle || matchesDescription || matchesKeywords || matchesContent) {
      if (!results.find((r) => r.id === article.id)) {
        results.push(article);
      }
    }
  };

  categories.forEach((category) => {
    category.articles.forEach(searchArticle);
    category.subcategories?.forEach((sub) => {
      sub.articles.forEach(searchArticle);
    });
  });

  return results;
}
